import React, { Fragment, useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useSignIn, useSignOut } from "react-auth-kit";
import {
  createClientIdQueryString,
  createQueryString,
  sendApiRequest,
} from "../../services/Base/base-service";
import {
  flowTypes,
  notifyUser,
  setFormatHandler,
  showFlowType,
} from "../../services/helpers";

import UserContext from "../../store/user-context";
import {
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  FormGroup,
  Form,
  Input,
  Toggle,
  Select,
  Button,
  Alert,
  Spinner,
} from "design-react-kit";

const ClientConfigurationModal = ({
  modalOpen,
  modalCloseHandler,
  selectedClient,
  setClients,
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [isAutomatic, setisAutomatic] = useState(false);
  const [isAddressGeolocationForced, setIsAddressGeolocationForced] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [continueModalOpen, setContinueModalOpen] = useState(false);
  const [client, setClient] = useState(null);
  const [error, setError] = useState(null);
  const [notificationCostPolicy, setNotificationCostPolicy] = useState(null);
  const [format, setFormat] = useState(null);
  const [analogicNotificationRequestType, setAnalogicNotificationRequestType] =
    useState(null);

  const navigate = useNavigate();
  const signIn = useSignIn();
  const signOut = useSignOut();

  const userContext = useContext(UserContext);

  const enableClientHandler = (e) => {
    setIsEnabled(e.target.checked);
    setClient((curState) => {
      return {
        ...curState,
        isConfigurationEnabled: e.target.checked,
      };
    });
  };

  const setIsAutomaticHandler = (e) => {
    setisAutomatic(e.target.checked);
    setClient((curState) => {
      return {
        ...curState,
        isFlowAutomaticallySent: e.target.checked,
      };
    });
  };

  const closeContinueModalHandler = () => {
    setClient(null);
    sendApiRequest(
      "ClientConfiguration/GetClientConfigurations",
      "GET",
      true,
      { fiscalCode: userContext.selectedClientFiscalCode, flowId: null },
      signIn,
      signOut,
      navigate,
      createQueryString,
      userContext
    ).then((response) => {
      if (response !== undefined) {
        if (response.errors !== null) {
          setError(response.errors);
          setIsLoading(false);
        } else {
          setClients(response.data.clientConfigurations);
          setIsLoading(false);
          modalCloseHandler();
          setContinueModalOpen(false);
        }
      }
    });
  };

  useEffect(() => {
    if (modalOpen) {
      setNotificationCostPolicy(null);
      setFormat(null);
      setAnalogicNotificationRequestType(null);
      setIsLoading(true);
      setError(null);
      setClient(null);
      sendApiRequest(
        "ClientConfiguration/GetSingleClientConfiguration",
        "GET",
        true,
        { clientId: selectedClient.clientId },
        signIn,
        signOut,
        navigate,
        createClientIdQueryString,
        userContext
      ).then((response) => {
        if (response !== undefined) {
          if (response.errors !== null) {
            setError(response.errors);
            setIsLoading(false);
          } else {
            setFormatHandler(response.data.flowType, setFormat);
            setNotificationCostPolicyHandler(
              response.data.notificationCostPolicy
            );
            setAnalogicNotificationRequestTypeHandler(
              response.data.analogicalNotificationRequestType
            );
            setisAutomatic(response.data.isFlowAutomaticallySent);
            setIsAddressGeolocationForced(
              response.data.isAddressGeolocationForced
            );
            setIsEnabled(
              response.data.clientConfigurationStatusId === 5 ? true : false
            );
            if (
              response.data.clientConfigurationStatusId !== 2 &&
              response.data.clientConfigurationStatusId !== 3
            ) {
              setTimeout(() => {
                document.querySelector("input.isActive").checked =
                  response.data.clientConfigurationStatusId === 5
                    ? true
                    : false;
              }, 500);
            }
            setClient(response.data);
            setIsLoading(false);
          }
        }
      });
    } else {
      setContinueModalOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  useEffect(() => {
    if (modalOpen) {
      setTimeout(() => {
        document.querySelector("input.isAutomatic").checked = isAutomatic;
        if (
          client !== null &&
          client.clientConfigurationStatusId !== 2 &&
          client.clientConfigurationStatusId !== 3
        ) {
          document.querySelector("input.isActive").checked = isEnabled;
        }
      }, 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, isEnabled, isAutomatic]);

  useEffect(() => {
    if (modalOpen) {
      setTimeout(() => {
        document.querySelector("input.isAddressGeolocationForced").checked =
          isAddressGeolocationForced;
      }, 500);
    }
  }, [modalOpen, isAddressGeolocationForced]);

  const setNotificationCostPolicyHandler = (policy) => {
    if (policy === "FLAT_RATE") {
      setNotificationCostPolicy({ value: 1, label: policy });
    } else if (policy === "DELIVERY_MODE") {
      setNotificationCostPolicy({ value: 2, label: policy });
    }
  };

  const setAnalogicNotificationRequestTypeHandler = (type) => {
    if (type === "AR_REGISTERED_LETTER") {
      setAnalogicNotificationRequestType({ value: 1, label: type });
    } else if (type === "REGISTERED_LETTER_890") {
      setAnalogicNotificationRequestType({ value: 2, label: type });
    }
  };

  const handleNotificationCostPolicyChange = (selectedOption) => {
    setNotificationCostPolicy(selectedOption);
    setClient((curState) => {
      return {
        ...curState,
        notificationCostPolicy: selectedOption.label,
      };
    });
  };

  const handleFormatChange = (selectedOption) => {
    setFormat(selectedOption);
    setClient((curState) => {
      return {
        ...curState,
        flowType: selectedOption.value,
      };
    });
  };

  const handleAnalogicNotificationRequestType = (selectedOption) => {
    setAnalogicNotificationRequestType(selectedOption);
    setClient((curState) => {
      return {
        ...curState,
        analogicalNotificationRequestType: selectedOption.label,
      };
    });
  };

  const upsertClientConfiguration = (e) => {
    e.stopPropagation();
    setError(null);
    let hasError = false;

    let data;
    client.s3BucketName !== null && client.s3BucketName !== ""
      ? (data = client)
      : (data = {
          ...client,
          s3BucketName: `${selectedClient.clientCode.toLowerCase()}-${
            userContext.selectedClientFiscalCode
          }`,
        });

    if (client.clientId === null || client.clientId === 0) {
      data = { ...data, clientId: selectedClient.clientId };
    }

    if (
      data.documentsAvailabilityDays === null ||
      data.documentsAvailabilityDays === undefined ||
      data.documentsAvailabilityDays === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [
              ...prevState,
              `Il valore dei "Giorni di permanenza PDF già trasmessi a EN CORE" è obbligatorio`,
            ]
          : [
              `Il valore dei "Giorni di permanenza PDF già trasmessi a EN CORE" è obbligatorio`,
            ]
      );
    }

    if (
      data.awsLambdaInvokeMinutes === null ||
      data.awsLambdaInvokeMinutes === undefined ||
      data.awsLambdaInvokeMinutes === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [
              ...prevState,
              `Il valore dei "Frequenza in minuti (a partire dalle 00:00) di analisi documenti caricati dall'ente" è obbligatorio`,
            ]
          : [
              `Il valore dei "Frequenza in minuti (a partire dalle 00:00) di analisi documenti caricati dall'ente" è obbligatorio`,
            ]
      );
    }

    if (
      data.notificationCostPolicy === null ||
      data.notificationCostPolicy === undefined ||
      data.notificationCostPolicy === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [
              ...prevState,
              `Il valore dei "Costi di notifica: policy" è obbligatorio`,
            ]
          : [`Il valore dei "Costi di notifica: policy" è obbligatorio`]
      );
    }

    if (
      data.analogicalNotificationRequestType === null ||
      data.analogicalNotificationRequestType === undefined ||
      data.analogicalNotificationRequestType === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [
              ...prevState,
              `Il valore dei "Notifica analogica: tipo notifica" è obbligatorio`,
            ]
          : [`Il valore dei "Notifica analogica: tipo notifica" è obbligatorio`]
      );
    }

    if (
      data.abilitationGroup === null ||
      data.abilitationGroup === undefined ||
      data.abilitationGroup === ""
    ) {
      hasError = true;
      setError((prevState) =>
        prevState !== null
          ? [...prevState, `Il valore dei "Gruppo Abilitato" è obbligatorio`]
          : [`Il valore dei "Gruppo Abilitato" è obbligatorio`]
      );
    }

    if (!hasError) {
      setIsLoading(true);
      sendApiRequest(
        `ClientConfiguration/UpsertClientConfiguration`,
        "POST",
        true,
        data,
        signIn,
        signOut,
        navigate,
        null,
        userContext
      ).then((response) => {
        if (response !== undefined) {
          if (response.errors !== null) {
            setError(response.errors);
            setIsLoading(false);
          } else {
            if (data.clientConfigurationStatusId === 2) {
              setContinueModalOpen(true);
            } else {
              modalCloseHandler();
              notifyUser(
                client.s3BucketName !== null
                  ? "Aggiornamento configurazione per tipologia servizio completato con successo!"
                  : "Creazione configurazione per tipologia servizio completata con successo!",
                "success",
                "it-check"
              );
            }
          }
        }
      });
    }
  };

  return (
    <Modal
      isOpen={modalOpen}
      toggle={modalCloseHandler}
      align="center"
      scrollable
      labelledBy="clientConfiguration"
      id="clientConfigurationModal"
    >
      <ModalHeader
        toggle={modalCloseHandler}
        id="clientConfiguration"
        className="mb-4 w-50"
      >
        <h5 className="text-capitalize">
          Tipologia Servizio:{" "}
          <span style={{ color: "#0073e6 " }}>
            {selectedClient?.clientCode}
          </span>
        </h5>
        <h6 className="text-capitalize" style={{ color: "#0073e6 " }}>
          {selectedClient?.clientCodeName}
        </h6>
      </ModalHeader>
      <ModalBody>
        {isLoading && <Spinner double active />}
        {!isLoading && error !== null && error.length !== undefined && (
          <Alert color="danger">
            {error.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </Alert>
        )}
        {client !== null && client !== undefined && (
          <Form className="mr-auto ml-auto">
            <Fragment>
              {client.s3BucketName !== "" && client.s3BucketName !== null && (
                <div className="p-input-wrapper">
                  <div className="p-label" htmlFor="bucket">
                    Nome dell' AWS Bucket
                  </div>
                  <p id="bucket" className="p-input">
                    {client.s3BucketName}
                  </p>
                </div>
              )}
              {client.awsAccessKey !== "" && (
                <div className="p-input-wrapper">
                  <div className="p-label" htmlFor="accessKey">
                    AWS Access Key
                  </div>
                  <p id="accessKey" className="p-input">
                    {client.awsAccessKey}
                  </p>
                </div>
              )}
              {client.awsSecretKey !== "" && (
                <div className="p-input-wrapper">
                  <div className="p-label" htmlFor="secretKey">
                    AWS Secret Key
                  </div>
                  <p id="secretKey" className="p-input">
                    {client.awsSecretKey}
                  </p>
                </div>
              )}
              <div className="form-row mb-4">
                <FormGroup className="col-sm-12 m-0 position-relative">
                  <Input
                    id="taxonomyCode"
                    type="text"
                    label="Codice Tassonomico"
                    placeholder="Inserisci Codice Tassonomico"
                    value={client.taxonomyCode}
                    onChange={(e) =>
                      setClient((curState) => {
                        return {
                          ...curState,
                          taxonomyCode: e.target.value,
                        };
                      })
                    }
                  />
                </FormGroup>
              </div>
              {client.flowType !== null && client.flowType !== undefined && (
                <div className="p-input-wrapper">
                  <div className="p-label" htmlFor="secretKey">
                    Fromato
                  </div>
                  <p id="secretKey" className="p-input">
                    {showFlowType(client.flowType)}
                  </p>
                </div>
              )}
              {(client.flowType === null || client.flowType === undefined) && (
                <div className="bootstrap-select-wrapper mb-5">
                  <label htmlFor="format">Formato</label>
                  <Select
                    id="format"
                    placeholder="Inserisci formato"
                    aria-label="Inserisci formato"
                    value={format}
                    onChange={handleFormatChange}
                    options={flowTypes}
                  />
                </div>
              )}
              <div className="form-row">
                <FormGroup className="col-sm-12 m-0 position-relative">
                  <Input
                    id="documentAvailabilityDays"
                    type="number"
                    min={0}
                    label="Giorni di permanenza PDF già trasmessi a EN CORE"
                    placeholder="Inserisci Giorni di permanenza PDF già trasmessi a EN CORE"
                    value={client.documentsAvailabilityDays}
                    onChange={(e) =>
                      setClient((curState) => {
                        return {
                          ...curState,
                          documentsAvailabilityDays: e.target.value,
                        };
                      })
                    }
                  />
                </FormGroup>
              </div>
              {client.sourceFolder !== "" && (
                <div className="p-input-wrapper">
                  <div className="p-label" htmlFor="secretKey">
                    Cartella SFTP Documenti
                  </div>
                  <p id="secretKey" className="p-input">
                    {client.sourceFolder}
                  </p>
                </div>
              )}
              <div className="form-row">
                <FormGroup className="col-sm-12 m-0 position-relative">
                  <Input
                    id="lambdaInvokeMinutes"
                    type="number"
                    min={0}
                    max={24}
                    label="Frequenza in minuti (a partire dalle 00:00) di analisi documenti caricati dall'ente"
                    placeholder="Inserisci Frequenza (ore dalle 00:00) analisi documenti caricati dall'ente"
                    value={client.awsLambdaInvokeMinutes}
                    onChange={(e) =>
                      setClient((curState) => {
                        return {
                          ...curState,
                          awsLambdaInvokeMinutes: e.target.value,
                        };
                      })
                    }
                  />
                </FormGroup>
              </div>
              <div className="bootstrap-select-wrapper mb-5">
                <label htmlFor="notificationPolicyCost">
                  Costi di notifica: policy
                </label>
                <Select
                  id="notificationPolicyCost"
                  placeholder="Inserisci Costi di notifica: policy"
                  aria-label="Inserisci Costi di notifica: policy"
                  value={notificationCostPolicy}
                  onChange={handleNotificationCostPolicyChange}
                  options={[
                    {
                      value: 1,
                      label: "FLAT_RATE",
                    },
                    {
                      value: 2,
                      label: "DELIVERY_MODE",
                    },
                  ]}
                />
              </div>
              <div className="bootstrap-select-wrapper mb-5">
                <label htmlFor="analogNotificationType">
                  Notifica analogica: tipo notifica
                </label>
                <Select
                  id="analogNotificationType"
                  placeholder="Inserisci Notifica analogica: tipo notifica"
                  aria-label="Inserisci Notifica analogica: tipo notifica"
                  value={analogicNotificationRequestType}
                  onChange={handleAnalogicNotificationRequestType}
                  options={[
                    {
                      value: 1,
                      label: "AR_REGISTERED_LETTER",
                    },
                    {
                      value: 2,
                      label: "REGISTERED_LETTER_890",
                    },
                  ]}
                />
              </div>
              <FormGroup className="col-sm-12 m-0">
                <Input
                  className="mb-5"
                  id="subject"
                  type="text"
                  label="Oggetto"
                  placeholder="Inserisci Oggetto"
                  value={client.notificationSubject}
                  onChange={(e) =>
                    setClient((curState) => {
                      return {
                        ...curState,
                        notificationSubject: e.target.value,
                      };
                    })
                  }
                />
              </FormGroup>
              <FormGroup className="col-sm-12 m-0">
                <Input
                  className="mb-5"
                  id="subject"
                  type="text"
                  label="Gruppo Abilitato"
                  placeholder="Inserisci Gruppo Abilitato"
                  value={client.abilitationGroup}
                  onChange={(e) =>
                    setClient((curState) => {
                      return {
                        ...curState,
                        abilitationGroup: e.target.value,
                      };
                    })
                  }
                />
              </FormGroup>
              <FormGroup
                check
                className="form-check-group d-flex align-items-center justify-content-start isAutomatic"
              >
                <Toggle
                  id="isAutomatic-toggle"
                  className="isAutomatic"
                  defaultChecked
                  onClick={(e) => setIsAutomaticHandler(e)}
                />
                <span id="isAutomatic" className="mb-2 toggles-span">
                  Autorizza invio automatico dei flussi predisposti
                </span>
              </FormGroup>
              <FormGroup
                check
                className="form-check-group d-flex align-items-center justify-content-start"
              >
                <Toggle
                  id="isAddressGeolocationForced-toggle"
                  className="isAddressGeolocationForced"
                  onClick={(e) =>
                    setClient((curState) => {
                      return {
                        ...curState,
                        isAddressGeolocationForced: e.target.checked,
                      };
                    })
                  }
                />
                <span className="mb-2 toggles-span">
                  Forza utilizzo dei servizi Google di Geolocalizzazione
                </span>
              </FormGroup>
              {client.clientConfigurationStatusId !== null &&
                client.clientConfigurationStatusId !== 2 &&
                client.clientConfigurationStatusId !== 3 && (
                  <FormGroup
                    check
                    className="form-check-group d-flex align-items-center justify-content-start mb-3 isActive"
                  >
                    <Toggle
                      id="isActive-toggle"
                      className="isActive"
                      onClick={(e) => enableClientHandler(e)}
                    />
                    <span className="mb-2 toggles-span" id="isActive">
                      Configurazione abilitata
                    </span>
                  </FormGroup>
                )}
            </Fragment>

            <FormGroup className="col-sm-12 mb-2 d-flex align-items-center justify-content-end">
              <Button
                id="user-cancel"
                color="white"
                className="mr-2"
                onClick={modalCloseHandler}
              >
                Annulla
              </Button>
              <Button
                style={{
                  backgroundColor: "#5EB423",
                  color: "#fff",
                  padding: "7px 15px",
                }}
                id="user-save"
                onClick={(e) => upsertClientConfiguration(e)}
              >
                <Icon
                  icon="it-check"
                  fill="#ffffff"
                  onClick={(e) => upsertClientConfiguration(e)}
                />
                Salva
              </Button>
            </FormGroup>
          </Form>
        )}
        {client !== null && client.clientConfigurationStatusId === 2 && (
          <Modal
            isOpen={continueModalOpen}
            toggle={closeContinueModalHandler}
            align="center"
            scrollable
            labelledBy="confirm"
          >
            <ModalHeader
              toggle={closeContinueModalHandler}
              id="confirm"
            ></ModalHeader>
            <ModalBody className="text-center">
              <div className="mb-3">
                <Icon icon="it-info-circle" fill="#5EB423" />
                <h5 className="d-inline font-weight-bold ml-2">
                  Continua a configurare!
                </h5>
              </div>
              <p className="mb-2 font-weight-normal">
                {client !== null &&
                  client.s3BucketName === null &&
                  "Creazione configurazione per tipologia di servizio completata con successo!"}
                {client !== null &&
                  client.s3BucketName !== null &&
                  "Aggiornamento configurazione per tipologia di servizio completato con successo!"}
              </p>
              <p className="mb-2 font-weight-normal">
                Vuoi continuare la configurazione per questa tipologia di
                servizio?
              </p>
              <div className="col-sm-12 mb-2 d-flex align-items-center justify-content-around">
                <Button
                  color="white"
                  className="mr-2"
                  onClick={closeContinueModalHandler}
                  id="user-cancel"
                >
                  No
                </Button>
                <Button
                  style={{
                    backgroundColor: "#5EB423",
                    color: "#fff",
                    padding: "7px 15px",
                  }}
                  color="success"
                  onClick={(e) =>
                    navigate(
                      `/clientConfiguration/clientDocumentsConfiguration/${selectedClient?.clientCodeName}/${selectedClient.clientId}`
                    )
                  }
                >
                  Si
                </Button>
              </div>
            </ModalBody>
          </Modal>
        )}
      </ModalBody>
    </Modal>
  );
};

export default ClientConfigurationModal;
